import type { ComponentType } from 'react'
import { Search, KeyRound, HeartHandshake } from 'lucide-react'
import { useDemoFlag } from '@/lib/demo-flags'
import { resolveSite, type SiteId } from '@/lib/site'
import { useUi, type UiStrings } from '@/lib/i18n/ui'

type IconCmp = ComponentType<{ className?: string }>

export type Tool = {
  slug: string
  to: string
  label: string
  body: string
  icon: IconCmp
  /**
   * Whether the tool actually works today. Ken's note (2026-08-18): showing
   * a feature that isn't available is confusing, so unavailable tools are
   * hidden entirely rather than shown with a "Soon" badge. They become
   * upgrades we offer existing clients later.
   */
  available: boolean
  /** Why it's unavailable. Kept so the next person doesn't have to re-derive it. */
  blockedBy?: string
  /**
   * Faces where the tool is hidden even though it works: the charity checker
   * reads the US IRS register, so it has nothing to say to a Qatari customer.
   */
  hiddenOn?: SiteId[]
  /** Dictionary keys for the bilingual faces; English faces use label/body. */
  i18n: { label: keyof UiStrings; body: keyof UiStrings }
}

export const TOOLS: Tool[] = [
  {
    slug: 'password-check',
    to: '/tools/password-check',
    label: 'Password Check',
    body: 'See if a password has shown up in known data breaches. Runs entirely in your browser, we never see what you typed.',
    icon: KeyRound,
    // Calls api.pwnedpasswords.com directly with k-anonymity, so it needs no
    // backend of ours and works on the ungated site today (verified live).
    available: true,
    i18n: { label: 'toolPasswordLabel', body: 'toolPasswordBody' },
  },
  {
    slug: 'scam-checker',
    to: '/tools/scam-checker',
    label: 'Scam Checker',
    body: 'Paste a suspicious link, message, or situation and get a plain-English read on whether it looks legit.',
    icon: Search,
    // Live against granguard-api POST /v1/public/scam-check (2026-09-02):
    // anonymous, aiLimiter-rate-limited, runs the full engine.
    available: true,
    i18n: { label: 'toolScamCheckerLabel', body: 'toolScamCheckerBody' },
  },
  {
    slug: 'charity-checker',
    to: '/tools/charity-checker',
    label: 'Charity Checker',
    body: 'Verify that a charity is real before you donate.',
    icon: HeartHandshake,
    // Live against granguard-api /v1/public/charity/search, which reads the
    // IRS Business Master File and Form 990 filings via ProPublica. US only.
    available: true,
    hiddenOn: ['qatar'],
    i18n: { label: 'toolCharityLabel', body: 'toolCharityBody' },
  },
]

/**
 * Tools to show in nav and on /tools. Unavailable ones stay hidden unless the
 * `?tools=all` demo flag is on, which lets a presenter walk a partner through
 * the roadmap without members ever seeing an unavailable feature.
 */
export function useVisibleTools(): Tool[] {
  const showAll = useDemoFlag('toolsPreview')
  const t = useUi()
  const site = resolveSite()
  const list = (showAll ? TOOLS : TOOLS.filter(t => t.available)).filter(
    tool => !tool.hiddenOn?.includes(site),
  )
  // Bilingual faces read label/body from the dictionary so the Arabic toggle
  // reaches the nav and the /tools grid; the dictionary's English matches the
  // registry text, so English faces render exactly what they always did.
  return list.map(tool => ({
    ...tool,
    label: t[tool.i18n.label] as string,
    body: t[tool.i18n.body] as string,
  }))
}

/** True when at least one tool is live, i.e. /tools is worth linking at all. */
export function useHasVisibleTools(): boolean {
  return useVisibleTools().length > 0
}
